import type {
  GraphEdgeSettings,
  GraphEdgeSettingsStyle,
  GroupEdgeSettings,
  PathLookupEdgeSettings,
  PathLookupEdgeType,
  ProtocolEdgeType,
  TopologyEdgeSettings,
} from '@ipfabric/utils';
import { moveEdgeSettings } from 'components/Graph/commands/moveEdgeSettings';
import { setDraggedGroupId } from 'components/Graph/commands/setDraggedGroupId';
import { sortGroups } from 'components/Graph/commands/sortGroups';
import type { GraphCommandContext } from 'components/Graph/model/GraphCommandContext';
import { type RefObject, useEffect } from 'react';
import { type DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { canSort } from 'utils/dnd/canSort';
import type { CommandDispatcher } from 'utils/react/useCommandDispatcher';
import type { DragProtocolItem } from '../TopologyLayoutSettingsProtocolsProtocol/useProtocolDragAndDrop';

interface Props {
  dispatch: CommandDispatcher<GraphCommandContext>;
  presetEditMode: boolean;
  groupRef: RefObject<HTMLDivElement>;
  groupIndex: number;
  groupId: GroupEdgeSettings['id'] | null;
  groupName: GroupEdgeSettings['name'];
  otherProtocols: boolean;
  protocols: (TopologyEdgeSettings | PathLookupEdgeSettings)[];
  style?: GraphEdgeSettingsStyle;
}

export interface DragGroupItem {
  groupId: GraphEdgeSettings['id'];
  groupIndex: number;
  groupName: ProtocolEdgeType | PathLookupEdgeType;
  protocols: (TopologyEdgeSettings | PathLookupEdgeSettings)[];
  style?: GraphEdgeSettingsStyle;
  type: 'group';
}

export function useGroupDragAndDrop(props: Props): void {
  const [, drag, preview] = useDrag({
    item: {
      groupIndex: props.groupIndex,
      groupId: props.groupId,
      groupName: props.groupName,
      protocols: props.protocols,
      type: 'group',
      style: props.style,
    },
    canDrag: () => {
      return props.presetEditMode && !props.otherProtocols;
    },
    begin: () => {
      setTimeout(() => {
        props.dispatch(setDraggedGroupId, { groupId: props.groupId });
      }, 0);
    },
    end: () => {
      props.dispatch(setDraggedGroupId, { groupId: null });
    },
  });

  const [, drop] = useDrop({
    accept: ['group', 'edge'],
    hover: (
      item: DragGroupItem | DragProtocolItem,
      monitor: DropTargetMonitor,
    ) => {
      if (!props.groupRef.current) {
        return;
      }
      const groupDragIndex = item.groupIndex;
      const groupHoverIndex = props.groupIndex;

      if (
        item.type === 'edge' ||
        (item.type === 'group' && props.otherProtocols)
      ) {
        return;
      }

      // Don't replace items with themselves
      if (groupDragIndex === groupHoverIndex) {
        return;
      }

      if (
        canSort(
          props.groupRef.current,
          monitor,
          groupDragIndex,
          groupHoverIndex,
        )
      ) {
        props.dispatch(sortGroups, {
          dragIndex: groupDragIndex,
          hoverIndex: groupHoverIndex,
        });

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.

        // Example: https://github.com/react-dnd/react-dnd/blob/09838c9c203554bfe23c97f7af82b120f7f28be8/packages/documentation/examples-hooks/src/04-sortable/simple/Card.tsx
        item.groupIndex = groupHoverIndex;
      }
    },
    drop: (item: DragProtocolItem) => {
      if (item.type === 'group' || item.groupIndex === props.groupIndex) {
        return;
      }
      // Move protocol to empty group
      props.dispatch(moveEdgeSettings, {
        dragGroupIndex: item.groupIndex,
        dragProtocolIndex: item.protocolIndex!,
        hoverGroupIndex: props.groupIndex,
        hoverProtocolIndex: -1,
      });
    },
  });

  // Initialize DnD when user starts edit.
  // Must not be initialized immediately because groupRef is not available before render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: initialize only when entering edit mode
  useEffect(() => {
    if (props.presetEditMode) {
      drag(drop(props.groupRef));
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [props.presetEditMode]);
}
